import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { FullScreenLoader } from '@/components/common/FullScreenLoader';
import { useCompleteCalendarAuthorizationMutation } from '@/hooks/useCalendarSync';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

export function CalendarSyncCallbackPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const completeAuthorization = useCompleteCalendarAuthorizationMutation();
  const hasProcessed = useRef(false);

  useEffect(
    function processCallback() {
      if (hasProcessed.current === true) {
        return;
      }
      hasProcessed.current = true;

      function returnToSettings() {
        navigate(APPLICATION_ROUTES.workspaceSettings, { replace: true });
      }

      const authorizationCode = searchParams.get('code');
      const state = searchParams.get('state');
      const authorizationError = searchParams.get('error');

      if (authorizationError !== null || authorizationCode === null || state === null) {
        showErrorNotification(t('calendarSync.callback.failed'));
        returnToSettings();
        return;
      }

      completeAuthorization.mutate(
        { code: authorizationCode, state },
        {
          onSuccess: function reportConnected() {
            showSuccessNotification(t('calendarSync.connected'));
            returnToSettings();
          },
          onError: function reportError(error) {
            const message = resolveApiErrorMessage(error);
            showErrorNotification(
              message.length > 0 ? message : t('calendarSync.callback.failed')
            );
            returnToSettings();
          },
        }
      );
    },
    [completeAuthorization, navigate, searchParams, t]
  );

  return <FullScreenLoader />;
}
